import { Client } from './client';
import { HttpClient } from './http-client';
import { TTLSet } from './ttl-set';
// Store latest identify payload for handshake pattern
let latestIdentifyPayload = null;
// Set up global message listener for iframe requests (only once)
if (typeof window !== 'undefined') {
    window.addEventListener('message', (event) => {
        const trustedOrigin = 'https://agent.brainfi.sh';
        if (event.origin !== trustedOrigin) {
            return; // Silently ignore messages from untrusted origins
        }
        // Handle REQUEST_USER_IDENTITY from agent-widget
        if (event.data?.type === 'REQUEST_USER_IDENTITY') {
            if (latestIdentifyPayload && event.source) {
                const iframe = document.querySelector('#bf-iframe-container .trigger-iframe');
                if (!iframe || event.source !== iframe.contentWindow) {
                    console.warn('BrainfishWidgetWarn: Rejected REQUEST_USER_IDENTITY from untrusted iframe source:', event.origin);
                    return;
                }
                // Send the latest identity to the verified iframe using the validated origin
                event.source.postMessage({
                    type: 'USER_IDENTIFY',
                    payload: latestIdentifyPayload,
                }, event.origin);
            }
        }
    });
}
export var NudgeCTABlockActionType;
(function (NudgeCTABlockActionType) {
    NudgeCTABlockActionType["OpenUrl"] = "openUrl";
    NudgeCTABlockActionType["CallPhone"] = "callPhone";
    NudgeCTABlockActionType["SendEmail"] = "sendEmail";
    NudgeCTABlockActionType["TriggerEvent"] = "triggerEvent";
})(NudgeCTABlockActionType || (NudgeCTABlockActionType = {}));
export var NudgeCTABlockType;
(function (NudgeCTABlockType) {
    NudgeCTABlockType["Button"] = "button";
    NudgeCTABlockType["Link"] = "link";
})(NudgeCTABlockType || (NudgeCTABlockType = {}));
export class TrackerSdk {
    options;
    client;
    httpClient;
    userId;
    global;
    queue = [];
    knownEvents = ['nudge.show'];
    eventListeners = {};
    // this set is used to prevent showing the same nudge multiple times in a short period
    recentNudgeIds = new TTLSet(1000);
    trackingDisabled = false;
    static TRACKING_DISABLED_KEY = 'bf_tracking_disabled';
    constructor(options) {
        this.options = options;
        const query = {
            sdk_name: options.sdk || 'node',
            sdk_version: options.sdkVersion || '',
        };
        const baseUrl = options.apiUrl || 'https://analytic.brainfi.sh';
        const authToken = options.accessKey;
        this.client = new Client({
            baseUrl,
            authToken,
            query,
        });
        this.httpClient = new HttpClient({
            baseUrl,
            authToken,
            sdkMetadata: {
                name: options.sdk || 'node',
                version: options.sdkVersion || '',
            },
        });
        // Check localStorage for tracking disabled state (only in browser environment)
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            this.trackingDisabled =
                localStorage.getItem(TrackerSdk.TRACKING_DISABLED_KEY) === 'true';
        }
        this.setupIncomingPayloadHandler();
    }
    // placeholder for future use
    init() {
        // empty
    }
    ready() {
        this.options.waitForUser = false;
        this.flush();
    }
    setupIncomingPayloadHandler() {
        this.client.on('e', this.handleIncomingPayload.bind(this));
    }
    handleIncomingPayload(payload) {
        switch (payload.type) {
            case 'nudge.show': {
                const { nudgeId } = payload.payload;
                if (this.recentNudgeIds.has(nudgeId)) {
                    return;
                }
                this.recentNudgeIds.add(nudgeId);
                this.dispatchEventListeners('nudge.show', payload.payload);
            }
        }
    }
    dispatchEventListeners(event, payload) {
        this.eventListeners[event]?.forEach((handler) => handler(payload));
    }
    on(event, handler) {
        if (!this.knownEvents.includes(event)) {
            // TODO: log an error?
            return;
        }
        if (!this.eventListeners[event]) {
            this.eventListeners[event] = [];
        }
        this.eventListeners[event].push(handler);
    }
    off(event, handler) {
        if (!this.knownEvents.includes(event)) {
            // TODO: log an error?
            return;
        }
        if (!this.eventListeners[event]) {
            return;
        }
        this.eventListeners[event] = this.eventListeners[event].filter((h) => h !== handler);
        if (this.eventListeners[event].length === 0) {
            delete this.eventListeners[event];
        }
    }
    async send(payload, responseCallback) {
        if (this.options.disabled) {
            return Promise.resolve();
        }
        if (this.trackingDisabled) {
            return Promise.resolve();
        }
        if (this.options.filter && !this.options.filter(payload)) {
            return Promise.resolve();
        }
        if (this.options.waitForUser && !this.userId) {
            this.queue.push(payload);
            return Promise.resolve();
        }
        return this.client.send('t', payload, responseCallback);
    }
    setGlobalProperties(properties) {
        this.global = {
            ...this.global,
            ...properties,
        };
    }
    async track(name, properties, widgetSourceOverrides) {
        return this.send({
            type: 'event.track',
            payload: {
                name,
                userId: properties?.userId ?? this.userId,
                properties: {
                    ...(this.global ?? {}),
                    ...(properties ?? {}),
                    ...(widgetSourceOverrides
                        ? { _bfOverrides: widgetSourceOverrides }
                        : {}), // only include jwt overrides if they are provided
                },
            },
        });
    }
    async identify(payload) {
        if (payload.userId) {
            this.userId = payload.userId;
            this.flush();
        }
        else {
            console.warn('BrainfishWidgetWarn: `.identify` called without a userId. User identification requires a valid userId to be provided.');
        }
        // Validate that only main fields are present
        const allowedFields = new Set([
            'userId',
            'firstName',
            'lastName',
            'email',
            'phone',
            'avatar',
            'properties',
        ]);
        const extraFields = Object.keys(payload).filter((key) => !allowedFields.has(key));
        if (extraFields.length > 0) {
            console.warn(`BrainfishWidgetWarn: The following fields are not recognized as main identifier fields: ${extraFields.join(', ')}. Please place custom fields under the 'properties' object instead. Note: Using fields incorrectly may cause user identification to not work as expected.`);
        }
        // Validate phone format if provided
        if (payload.phone && !/^\+[1-9]\d{1,14}$/.test(payload.phone)) {
            console.warn('BrainfishWidgetWarn: phone should be in E.164 format (e.g., +14155552671). Providing the correct format ensures accurate user identification.');
        }
        // Store the latest payload for handshake pattern (iframe will request if needed)
        latestIdentifyPayload = payload;
        // Also try to send immediately if iframe exists (proactive push)
        const iframe = document.querySelector('#bf-iframe-container .trigger-iframe');
        if (iframe) {
            const sendMessage = () => {
                if (iframe.contentWindow) {
                    iframe.contentWindow.postMessage({
                        type: 'USER_IDENTIFY',
                        payload,
                    }, '*');
                }
            };
            // Try to send immediately if contentWindow exists
            if (iframe.contentWindow) {
                sendMessage();
            }
            // Also attach a load listener to ensure the message is sent
            // This covers the case where the iframe wasn't ready initially
            iframe.addEventListener('load', sendMessage);
        }
        if (Object.keys(payload).length > 1) {
            return this.send({
                type: 'user.identify',
                payload: {
                    ...payload,
                    properties: {
                        ...this.global,
                        ...payload.properties,
                    },
                },
            });
        }
    }
    async record(events) {
        if (this.options.disabled) {
            return Promise.resolve(null);
        }
        if (this.trackingDisabled) {
            return Promise.resolve(null);
        }
        const payload = {
            type: 'event.record',
            payload: {
                events,
                userId: this.userId,
            },
        };
        if (this.options.filter && !this.options.filter(payload)) {
            return Promise.resolve(null);
        }
        const response = await this.httpClient.send('/s', {
            events,
            userId: this.userId,
        });
        return response?.sessionId || null;
    }
    async alias(payload) {
        return this.send({
            type: 'user.alias',
            payload,
        });
    }
    async increment(payload) {
        return this.send({
            type: 'user.increment',
            payload,
        });
    }
    async decrement(payload) {
        return this.send({
            type: 'user.decrement',
            payload,
        });
    }
    clear() {
        this.userId = undefined;
        // should we force a session end here?
    }
    flush() {
        this.queue.forEach((item) => {
            this.send({
                ...item,
                payload: {
                    ...item.payload,
                    userId: item.payload.userId ?? this.userId,
                },
            });
        });
        this.queue = [];
    }
    /**
     * Disables privacy-sensitive tracking features (recording, screenshots).
     * This setting persists across page reloads via localStorage.
     */
    disableTracking() {
        this.trackingDisabled = true;
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            localStorage.setItem(TrackerSdk.TRACKING_DISABLED_KEY, 'true');
        }
    }
    /**
     * Re-enables privacy-sensitive tracking features (recording, screenshots).
     * Note: Full effect requires a page reload to restart recording.
     */
    enableTracking() {
        this.trackingDisabled = false;
        if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
            localStorage.removeItem(TrackerSdk.TRACKING_DISABLED_KEY);
        }
        console.log('Brainfish tracking re-enabled. Refresh page for full effect.');
    }
    /**
     * Returns whether privacy-sensitive tracking is currently disabled.
     * @returns true if tracking is disabled, false otherwise
     */
    isTrackingDisabled() {
        return this.trackingDisabled;
    }
}
